import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';
import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import PatientFeedbackService from 'apps/wrapper/src/services/patient-feedback-service/patient-feedback-service';
import {
  FeedbackType,
  PatientFeedback
} from 'apps/wrapper/src/services/patient-feedback-service/patient-feedback-service-types';
import moment from 'moment-timezone';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { useCreateReviewSurvey, useIsReviewSurveyEnabled } from '../../../../../../hooks/use-review-survey';
import { NoDataComponent } from '../../../../../shared/grid/no-data';
import { ReviewSummaryDialog } from '../../../../../shared/patient-feedback/dialogs/review-summary/review-summary';
import { SurveyResultsDialog } from '../../../../../shared/patient-feedback/dialogs/survey-results/survey-results';
import { StarRating } from '../../../../../shared/star-rating/star-rating';
import { PatientProfileHeader } from '../../header/header';
import { QuickNavMenu } from '../../header/quick-nav-menu/quick-nav-menu';
import { DotSeparatedTitle, LiSeparator, List, ListItem } from '../../patient-profile-widget.styles';
import { ViewContent } from '../shared/view-content/view-content';
import { NotesSkeleton } from './patient-feedback.skeleton';
import { FeedbackContent, FeedbackInfo, FeedbackText, Toolbar } from './patient-feedback.styles';

export const PatientFeedbackView: FC = () => {
  const { config } = useConfig();
  const oidc = useOidcWithAdmin();
  const { showError } = useNotification();
  const { contactDetails, contactLocation, patientInfo, locationId, loggedInUserLocation } = usePatientProfile();
  const [patientFeedback, setPatientFeeedback] = useState<PatientFeedback[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSurvey, setIsLoadingSurvey] = useState(false);
  const [isLoadingReview, setIsLoadingReview] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(null);
  const [selectedSurveyId, setSelectedSurveyId] = useState<number | null>(null);

  const patientFeedbackService = useMemo(
    () => PatientFeedbackService.getInstance(config, oidc.oidcService),
    [config, oidc]
  );

  const baseUrl = useMemo(
    () =>
      `${window.location.protocol}//${window.location.hostname}${
        window.location.port ? `:${window.location.port}` : ''
      }`,
    [window.location]
  );

  const isReviewSurveyEnabled = useIsReviewSurveyEnabled({
    contactDetails,
    contactLocation,
    patientInfo,
    loggedInUserLocation
  });

  const { request } = useCreateReviewSurvey({ contactDetails, locationId });

  const loadPatientFeedback = useCallback(async () => {
    setIsLoading(true);

    try {
      const feedback = await patientFeedbackService.getPatientFeedback(contactDetails.id, locationId);

      setPatientFeeedback(feedback);
    } catch {
      showError('Unable to load patient feedback.');
    } finally {
      setIsLoading(false);
    }
  }, [contactDetails, locationId]);

  useEffect(() => {
    loadPatientFeedback();
  }, []);

  const handleFeedbackItemClick = useCallback((feedback: PatientFeedback) => {
    if (config.contact.fullPatientProfilePageEnabled === 'true') {
      if (feedback.feedbackType === FeedbackType.Review) {
        setSelectedReviewId(feedback.idPatientFeedback);
      } else {
        setSelectedSurveyId(feedback.idPatientFeedback);
      }
    } else {
      const url =
        feedback.feedbackType === FeedbackType.Review
          ? `${baseUrl}/core/patientfeedback/reviewdetails/${feedback.idPatientFeedback}`
          : `${baseUrl}/core/patientfeedback/surveydetails/${feedback.idPatientFeedback}`;

      window.open(url, '_blank');
    }
  }, []);

  const handleSendSurveyClick = async () => {
    setIsLoadingSurvey(true);
    await request(FeedbackType.Survey);
    setIsLoadingSurvey(false);
  };

  const handleSendReviewClick = async () => {
    setIsLoadingReview(true);
    await request(FeedbackType.Review);
    setIsLoadingReview(false);
  };

  return (
    <>
      <PatientProfileHeader />
      <QuickNavMenu />
      <ViewContent>
        <FeedbackContent>
          {isLoading && <NotesSkeleton />}
          {!isLoading && patientFeedback.length === 0 && <NoDataComponent />}
          {!isLoading && patientFeedback.length > 0 && (
            <Scrollbars autoHide style={{ flexGrow: 1 }}>
              <List>
                {patientFeedback.map((item, index) => (
                  <>
                    <ListItem key={item.idPatientFeedback} onClick={() => handleFeedbackItemClick(item)}>
                      <FeedbackInfo>
                        <DotSeparatedTitle
                          leftTitle={item.date ? moment(item.date).format('MM/DD/YYYY') : null}
                          rightTitle={item.feedbackType}
                        />
                        {item.feedbackType === FeedbackType.Review && <StarRating rating={item.ratingScore} />}
                        {item.feedbackType === FeedbackType.Survey && (
                          <Box sx={{ fontWeight: 500, color: 'rgba(6, 117, 221, 1)' }}>{item.ratingScore}%</Box>
                        )}
                      </FeedbackInfo>
                      <FeedbackText>{item.message}</FeedbackText>
                    </ListItem>
                    {index !== patientFeedback.length - 1 && <LiSeparator key={String(1000 + index)} />}
                  </>
                ))}
              </List>
            </Scrollbars>
          )}
          {!isLoading && (
            <Toolbar sx={{ display: 'flex', flexDirection: 'row' }}>
              <LoadingButton
                loading={isLoadingSurvey}
                disabled={!isReviewSurveyEnabled || isLoading || isLoadingReview}
                onClick={handleSendSurveyClick}
                variant="contained"
                disableElevation
                fullWidth
              >
                Send Survey
              </LoadingButton>
              <LoadingButton
                loading={isLoadingReview}
                disabled={!isReviewSurveyEnabled || isLoading || isLoadingSurvey}
                onClick={handleSendReviewClick}
                variant="contained"
                disableElevation
                fullWidth
                sx={{ marginLeft: '10px' }}
              >
                Send Review
              </LoadingButton>
            </Toolbar>
          )}
        </FeedbackContent>
      </ViewContent>
      {selectedReviewId && (
        <ReviewSummaryDialog
          reviewId={selectedReviewId}
          open={selectedReviewId != null}
          onClose={() => setSelectedReviewId(null)}
        />
      )}
      {selectedSurveyId && (
        <SurveyResultsDialog
          surveyId={selectedSurveyId}
          open={selectedSurveyId != null}
          onClose={() => setSelectedSurveyId(null)}
        />
      )}
    </>
  );
};
